import React, { useState, useEffect } from 'react'
import apiService from 'services/api'
import { StatsCard, StatsContainer } from '../../styled/Stats'
import FbButton from 'components/FbUI/FbButton'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { PurchaseCreditsModal } from 'components/Modals/PurchaseCreditsModal'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { featureFlagService } from '../../utils/featureFlagService'
import { useAuth } from 'context/authentication/useAuth'
import { formatInteger } from 'utils/formatting'
import { CompanyEmailExtension } from 'models/companies'

interface CreditBalanceCardProps {
  className?: string
  refreshTrigger?: number
}

interface Company {
  id: number
  name: string
  logo: string | null
  email_extensions: Array<CompanyEmailExtension>
  status: string
  stripe_customer_id: string | null
  billing_email: string | null
  rebate_support_email: string | null
  is_activate: boolean
  contract_start_date: string | null
}

interface CreditBalance {
  total: number
  available: number
  used: number
  pending: number
}

const initialCreditBalance: CreditBalance = {
  total: 0,
  available: 0,
  used: 0,
  pending: 0,
}

interface CreditStat {
  name: string
  key: keyof CreditBalance
  tooltip: string
}

export function CreditBalanceCard({
  className = '',
  refreshTrigger = 0,
}: CreditBalanceCardProps) {
  const [creditBalance, setCreditBalance] =
    useState<CreditBalance>(initialCreditBalance)
  const [loading, setLoading] = useState(false)
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const { user } = useAuth()
  const navigate = useNavigate()
  const isActivate =
    (user?.company as Company | undefined)?.is_activate ?? false
  const hasStripeCustomerId = !!user?.company?.stripe_customer_id
  const { enableContactCredits } = featureFlagService()
  const api = apiService()

  // Check if the user has unlimited credits (activated or feature flag is off)
  const hasUnlimitedCredits = isActivate || !enableContactCredits

  const handleBuyCreditsClick = () => {
    if (!hasStripeCustomerId) {
      toast.warning(
        'Please configure billing in settings before purchasing credits.',
        {
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          onClick: () => navigate('/settings/billing'),
        }
      )

      return
    }
    setShowPurchaseModal(true)
  }

  const creditStats: CreditStat[] = [
    {
      name: 'Credits Used',
      key: 'used' as const,
      tooltip: 'Total number of credits spent on completed contact requests',
    },
    ...(hasUnlimitedCredits
      ? []
      : [
          {
            name: 'Credits Pending',
            key: 'pending' as const,
            tooltip: 'Credits reserved for in-progress contact requests',
          },
        ]),
    ...(enableContactCredits
      ? [
          {
            name: 'Credits Remaining',
            key: 'available' as const,
            tooltip: 'Number of credits you can use for new contact requests',
          },
        ]
      : []),
  ]

  useEffect(() => {
    let isMounted = true

    const loadCreditBalance = async () => {
      try {
        setLoading(true)
        const response = await api.fetchCreditBalance()

        if (isMounted && response?.credits) {
          setCreditBalance({
            total: response.credits.total || 0,
            available: response.credits.available || 0,
            used: response.credits.used || 0,
            pending: response.credits.pending || 0,
          })
        }
      } catch (error) {
        console.error('Failed to fetch credit balance:', error)
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }
    loadCreditBalance()

    return () => {
      isMounted = false
    }
  }, [refreshTrigger, isActivate])

  return (
    <div className={`flex flex-col relative ${className}`}>
      <StatsContainer className="mb-2">
        {creditStats.map((stat) => (
          <StatsCard key={stat.key}>
            <div className="flex justify-between w-full items-center">
              <h1>{stat.name}&nbsp;</h1>
              <HelpTooltip content={stat.tooltip} />
            </div>
            {!loading ? (
              <span>
                {hasUnlimitedCredits && stat.key === 'available'
                  ? '∞'
                  : formatInteger(creditBalance[stat.key])}
              </span>
            ) : (
              <span style={{ fontSize: 12, fontWeight: 400 }}>
                Calculating...
              </span>
            )}
          </StatsCard>
        ))}
      </StatsContainer>
      {!hasUnlimitedCredits && (
        <div className="relative z-10">
          <FbButton className="w-full" onClick={handleBuyCreditsClick}>
            Buy Credits
          </FbButton>
          <PurchaseCreditsModal
            open={showPurchaseModal}
            onClose={() => setShowPurchaseModal(false)}
          />
        </div>
      )}
    </div>
  )
}
